import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, defer, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from 'src/app/service/auth.service';
import { SubmissionsService } from 'src/app/service/submissions.service';
import { ReviewsService } from 'src/app/service/reviews.service';
import { EventsService } from 'src/app/service/events.service';
import { UserService } from 'src/app/service/user.service';
import { SHOW_ALL_PAGINATION_OPTIONS, NO_PAGINATION } from 'src/app/model/pagination.options';
import { SubmissionFilter } from 'src/app/model/submission.filter';
import { UserEventRole } from 'src/app/enum/user.event.role';

interface PaletteItem {
  // Texto a exibir (já traduzido OU literal vindo do backend).
  label: string;
  // Sublabel opcional (ex.: "SBRC 2026", "Paper #123").
  sublabel?: string;
  // Material Symbols ligature.
  icon: string;
  // Rota Angular para navegar.
  route?: string;
  // Ação custom.
  action?: () => void;
  // Agrupamento visual.
  group: 'nav' | 'submissions' | 'reviews' | 'committees' | 'chairings' | 'account' | 'actions';
  // Texto "buscável" — usado para filter. Combina label + sublabel + IDs.
  searchText: string;
}

@Component({
  selector: 'app-command-palette',
  templateUrl: './command-palette.component.html',
  styleUrls: ['./command-palette.component.scss'],
})
export class CommandPaletteComponent implements OnInit {
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef<HTMLInputElement>;

  query = '';
  selectedIndex = 0;
  isLoadingContent = true;

  // Itens estáticos (nav + account + actions).
  private staticItems: PaletteItem[] = [];
  // Itens de conteúdo (submissions/reviews/committees/chairings) carregados via API.
  private contentItems: PaletteItem[] = [];

  constructor(
    private dialogRef: MatDialogRef<CommandPaletteComponent>,
    private router: Router,
    private translate: TranslateService,
    private authService: AuthService,
    private submissionsService: SubmissionsService,
    private reviewsService: ReviewsService,
    private eventsService: EventsService,
    private userService: UserService,
  ) {}

  ngOnInit(): void {
    setTimeout(() => this.searchInput?.nativeElement?.focus(), 0);
    this.buildStaticItems();
    this.loadContent();
  }

  private buildStaticItems(): void {
    const t = (k: string) => this.translate.instant(k);
    this.staticItems = [
      { group: 'nav',     icon: 'emoji_objects', label: t('menus.sidebar.events.full'),         route: '/events',      searchText: '' },
      { group: 'nav',     icon: 'description',   label: t('menus.sidebar.my-submissions.full'), route: '/submissions', searchText: '' },
      { group: 'nav',     icon: 'balance',       label: t('menus.sidebar.my-reviews.full'),     route: '/reviews',     searchText: '' },
      { group: 'nav',     icon: 'groups',        label: t('menus.sidebar.my-committees.full'),  route: '/committees',  searchText: '' },
      { group: 'nav',     icon: 'star',          label: t('menus.sidebar.my-events.full'),      route: '/my-events',   searchText: '' },
      { group: 'account', icon: 'account_circle', label: t('menus.navbar.settings'), route: '/settings', searchText: '' },
      { group: 'account', icon: 'info',          label: t('menus.navbar.about'),    route: '/about',    searchText: '' },
      { group: 'account', icon: 'help',          label: t('menus.navbar.help'),     route: '/help',     searchText: '' },
      { group: 'actions', icon: 'logout',        label: t('menus.navbar.signout'),  action: () => this.authService.signOut(), searchText: '' },
    ];
    this.staticItems.forEach(item => { item.searchText = this.normalize(item.label); });
  }

  // Busca paralela das 4 listagens (submissions, reviews, committees, chairings).
  // Tudo client-side filterable depois.
  private loadContent(): void {
    this.userService.getUserFromToken().subscribe(user => {
      if (!user?.id) {
        this.isLoadingContent = false;
        return;
      }
      const userId = user.id;

      // Blindagem: cada service call vai em `defer` (lazy, captura erro
      // síncrono que alguns services lançam ao acessar `infoService.user.id`
      // se ainda undefined) + `catchError` (captura erro async). Fallback é
      // sempre uma Pagination vazia, garantindo que forkJoin completa.
      const safe = <T extends { items: any[] }>(call: () => Observable<T>, empty: T): Observable<T> =>
        defer(() => {
          try {
            return call().pipe(catchError(() => of(empty)));
          } catch {
            return of(empty);
          }
        });
      const emptyPagination: any = { items: [] };

      forkJoin({
        submissions: safe(() => this.submissionsService.getUserSubmissions(SHOW_ALL_PAGINATION_OPTIONS, new SubmissionFilter()), emptyPagination),
        reviews: safe(() => this.reviewsService.getUserReviews(SHOW_ALL_PAGINATION_OPTIONS, undefined, undefined, true), emptyPagination),
        committees: safe(() => this.eventsService.getEventByUserRole(userId, UserEventRole.COMMITTEE, NO_PAGINATION), emptyPagination),
        chairings: safe(() => this.eventsService.getEventByUserRole(userId, UserEventRole.CHAIR, NO_PAGINATION), emptyPagination),
      }).subscribe({
        next: ({ submissions, reviews, committees, chairings }) => {
          const items: PaletteItem[] = [];

          (submissions?.items || []).forEach((s: any) => {
            const label = s.title || `Paper #${s.id}`;
            const eventName = s.event ? `${s.event.acronym || ''} ${s.event.year || ''}`.trim() : '';
            items.push({
              group: 'submissions',
              icon: 'description',
              label,
              sublabel: eventName ? `${eventName} · #${s.id}` : `#${s.id}`,
              route: `/submissions/${s.id}`,
              searchText: this.normalize(`${label} ${eventName} ${s.id}`),
            });
          });

          (reviews?.items || []).forEach((r: any) => {
            const sub = r.submission;
            if (!sub) return;
            const label = sub.title || `Paper #${sub.id}`;
            const eventName = sub.event ? `${sub.event.acronym || ''} ${sub.event.year || ''}`.trim() : '';
            items.push({
              group: 'reviews',
              icon: 'balance',
              label,
              sublabel: eventName ? `${eventName} · #${sub.id}` : `#${sub.id}`,
              route: `/submissions/${sub.id}`,
              searchText: this.normalize(`${label} ${eventName} ${sub.id}`),
            });
          });

          (committees?.items || []).forEach((e: any) => {
            const label = e.name || e.acronym || `Event #${e.id}`;
            const acronym = `${e.acronym || ''} ${e.year || ''}`.trim();
            items.push({
              group: 'committees',
              icon: 'groups',
              label,
              sublabel: acronym || undefined,
              route: `/events/${e.id}`,
              searchText: this.normalize(`${label} ${acronym} ${e.id}`),
            });
          });

          (chairings?.items || []).forEach((e: any) => {
            const label = e.name || e.acronym || `Event #${e.id}`;
            const acronym = `${e.acronym || ''} ${e.year || ''}`.trim();
            items.push({
              group: 'chairings',
              icon: 'star',
              label,
              sublabel: acronym || undefined,
              route: `/events/${e.id}`,
              searchText: this.normalize(`${label} ${acronym} ${e.id}`),
            });
          });

          this.contentItems = items;
          this.isLoadingContent = false;
        },
        error: () => {
          this.isLoadingContent = false;
        },
      });
    });
  }

  // Lista filtrada. Quando query vazia, mostra só os estáticos + content.
  // Quando query presente, filtra ambos por searchText.
  get filteredItems(): PaletteItem[] {
    const q = this.normalize(this.query);
    const all = [...this.staticItems, ...this.contentItems];
    if (!q) {
      return all;
    }
    return all.filter(item => item.searchText.includes(q));
  }

  private normalize(value: string): string {
    return (value || '')
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');
  }

  get visibleGroups(): { key: PaletteItem['group']; titleKey: string; items: PaletteItem[] }[] {
    const filtered = this.filteredItems;
    const groups: { key: PaletteItem['group']; titleKey: string }[] = [
      { key: 'nav',         titleKey: 'command-palette.group-nav' },
      { key: 'submissions', titleKey: 'command-palette.group-submissions' },
      { key: 'reviews',     titleKey: 'command-palette.group-reviews' },
      { key: 'committees',  titleKey: 'command-palette.group-committees' },
      { key: 'chairings',   titleKey: 'command-palette.group-chairings' },
      { key: 'account',     titleKey: 'command-palette.group-account' },
      { key: 'actions',     titleKey: 'command-palette.group-actions' },
    ];
    return groups
      .map(g => ({ ...g, items: filtered.filter(i => i.group === g.key) }))
      .filter(g => g.items.length > 0);
  }

  onQueryChange(): void {
    this.selectedIndex = 0;
  }

  selectItem(item: PaletteItem): void {
    if (item.action) {
      item.action();
    } else if (item.route) {
      this.router.navigateByUrl(item.route);
    }
    this.dialogRef.close();
  }

  // trackBy functions — necessárias porque `filteredItems` é um getter que
  // retorna nova referência de array em cada chamada. Sem trackBy, o Angular
  // destruía/recriava todos os botões a cada change detection, fazendo o
  // click event não chegar na nova instância.
  trackGroup(_: number, group: { key: string }): string {
    return group.key;
  }
  trackItem(_: number, item: PaletteItem): string {
    return `${item.group}:${item.label}:${item.route || ''}`;
  }

  @HostListener('keydown', ['$event'])
  onKey(event: KeyboardEvent): void {
    const items = this.filteredItems;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.selectedIndex = (this.selectedIndex + 1) % items.length;
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.selectedIndex = (this.selectedIndex - 1 + items.length) % items.length;
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = items[this.selectedIndex];
      if (item) {
        this.selectItem(item);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.dialogRef.close();
    }
  }
}
